import { useCallback, useState } from "react";
import {
    applyCandidateStatus,
    hireCandidate,
    sendInterviewInvite,
    sendOffer,
    updateCandidate,
} from "../../services/candidateApi";
import { useAlertContext } from "../../context/AlertContext";
import { useAuth } from "../../context/AuthContext";
import OfferModal from "./OfferModal";
import InterviewInviteModal from "./InterviewInviteModal";
import HireCandidateModal from "./HireCandidateModal";
import ExerciseModal from "../calendar/ExerciseModal";
import InterviewReminderModal from "../calendar/InterviewReminderModal";
import { buildOfferLetterText } from "../../utils/interviewInvite";
import { HR_INTERVIEW_REMINDER_QUESTION } from "../../utils/interviewReminder";
import { DATE_STATUS_VALUES } from "../../utils/candidateStatuses";
import { rejectStatusFor } from "../../utils/candidateFunnel";

/**
 * Actions on a candidate for the list and table views of «Кандидаты» (правка №4/№14, Чат 1).
 * [Claude] 2026-11-03. Same behaviour as the old cards (CandidateList): HH invites, offers,
 * tests, hire, status with a date, comment. `patches` keep fresh values until the list reloads.
 */
function askStatusDate(showAlert) {
    const value = window.prompt("Дата статуса в формате ГГГГ-ММ-ДД", new Date().toISOString().slice(0, 10));
    if (!value) return null;
    const parsed = new Date(`${value}T00:00:00Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) {
        showAlert("Укажите корректную дату в формате ГГГГ-ММ-ДД", "warning");
        return null;
    }
    return value;
}

export function errorText(error) {
    const raw = error?.message || error;
    if (typeof raw === "string") return raw;
    if (raw && typeof raw === "object" && raw.detail) return String(raw.detail);
    try {
        return JSON.stringify(raw);
    } catch {
        return "Unknown error";
    }
}

export default function useCandidateActions({ onChange } = {}) {
    const { showAlert } = useAlertContext();
    const { user } = useAuth() || {};
    const [patches, setPatches] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [offerFor, setOfferFor] = useState(null);
    const [interviewFor, setInterviewFor] = useState(null);
    const [testFor, setTestFor] = useState(null);
    const [hireFor, setHireFor] = useState(null);
    const [hiring, setHiring] = useState(false);
    const [reminder, setReminder] = useState(null);

    const patch = useCallback((id, values) => {
        setPatches((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...values } }));
    }, []);

    const withPatch = useCallback((candidate) => (
        candidate && patches[candidate.id] ? { ...candidate, ...patches[candidate.id] } : candidate
    ), [patches]);

    const setStatus = useCallback(async (candidate, nextStatus, { silent = false, date = undefined } = {}) => {
        if (!candidate?.id || !nextStatus) return false;
        let statusDate = date ?? null;
        if (date === undefined && DATE_STATUS_VALUES.has(nextStatus)) {
            statusDate = askStatusDate(showAlert);
            if (!statusDate) return false;
        }
        try {
            setBusyId(candidate.id);
            await applyCandidateStatus(candidate.id, nextStatus, statusDate);
            patch(candidate.id, { status: nextStatus, current_status_date: statusDate });
            if (!silent) showAlert(`Статус: ${nextStatus}`, "success");
            onChange?.();
            return true;
        } catch (e) {
            const msg = errorText(e);
            if (!silent) {
                showAlert(/active vacancy not found|Active vacancy/i.test(msg)
                    ? "Сначала привяжите кандидата к вакансии — затем можно сменить статус."
                    : `Не удалось обновить статус: ${msg}`, /active vacancy/i.test(msg) ? "warning" : "error");
            }
            return false;
        } finally {
            setBusyId(null);
        }
    }, [onChange, patch, showAlert]);

    const reject = useCallback(async (candidate) => {
        const status = rejectStatusFor(withPatch(candidate)?.status);
        if (!window.confirm(`Отказать кандидату ${candidate?.full_name || ""}? Статус: «${status}».`)) return false;
        return setStatus(candidate, status);
    }, [setStatus, withPatch]);

    const saveComment = useCallback(async (candidate, text) => {
        try {
            await updateCandidate(candidate.id, { hr_comment: text || null });
            patch(candidate.id, { hr_comment: text || "" });
            showAlert("Комментарий сохранён", "success");
            return true;
        } catch (e) {
            showAlert(`Ошибка сохранения: ${errorText(e)}`, "error");
            return false;
        }
    }, [patch, showAlert]);

    const submitOffer = async (offer) => {
        if (!offerFor) return;
        try {
            await sendOffer(offerFor.id, offer);
            patch(offerFor.id, { status: "оффер: отправлен" });
            setOfferFor(null);
            showAlert("Оффер отправлен", "success");
            onChange?.();
        } catch (e) {
            showAlert(`Ошибка отправки оффера: ${errorText(e)}`, "error");
        }
    };

    const submitInterview = async (message, schedule, remind) => {
        if (!interviewFor?.id) return;
        try {
            const hrId = user?.erp_user_id || user?.id;
            const resp = await sendInterviewInvite(interviewFor.id, {
                message,
                hr_id: hrId || null,
                interview_date: schedule?.date,
                start_time: schedule?.startTime,
                end_time: schedule?.endTime,
                book_calendar: Boolean(hrId && schedule?.date && schedule?.startTime),
                remind_candidate: Boolean(remind?.enabled),
                remind_at: remind?.remind_at || null,
                reminder_message: remind?.message || null,
            });
            if (resp?.delivery && resp.delivery !== "sent" && resp.delivery !== "ok") {
                showAlert(resp?.warning || "Собеседование сохранено, но HH пока не принял приглашение.", "warning");
            } else {
                showAlert("Приглашение на собеседование отправлено кандидату в HH", "success");
            }
            patch(interviewFor.id, { status: "собес: назначен", current_status_date: schedule?.date || null });
            const invited = interviewFor;
            setInterviewFor(null);
            onChange?.();
            if (window.confirm(HR_INTERVIEW_REMINDER_QUESTION)) setReminder({ candidate: invited, schedule: schedule || null });
        } catch (e) {
            showAlert(`Ошибка отправки приглашения: ${errorText(e)}`, "error");
            throw e;
        }
    };

    const submitHire = async (terms) => {
        if (!hireFor?.id) return;
        try {
            setHiring(true);
            await hireCandidate(hireFor.id, terms);
            showAlert("Сотрудник добавлен. Дата выхода сохранена.", "success");
            setHireFor(null);
            onChange?.();
        } catch (e) {
            showAlert(`Не удалось добавить сотрудника: ${errorText(e)}`, "error");
        } finally {
            setHiring(false);
        }
    };

    const modals = (
        <>
            <OfferModal
                open={Boolean(offerFor)}
                defaultText={buildOfferLetterText(offerFor?.full_name)}
                defaultPosition={offerFor?.last_vacancy_title || offerFor?.position || ""}
                onClose={() => setOfferFor(null)}
                onSubmit={submitOffer}
            />
            <InterviewInviteModal
                open={Boolean(interviewFor)}
                candidate={interviewFor}
                onClose={() => setInterviewFor(null)}
                onSubmit={submitInterview}
            />
            <InterviewReminderModal
                open={Boolean(reminder)}
                candidate={reminder?.candidate}
                date={reminder?.schedule?.date || ""}
                startTime={reminder?.schedule?.startTime || ""}
                endTime={reminder?.schedule?.endTime || ""}
                onClose={() => setReminder(null)}
                onSkip={() => setReminder(null)}
                onSaved={() => setReminder(null)}
            />
            <HireCandidateModal
                open={Boolean(hireFor)}
                candidate={hireFor}
                busy={hiring}
                onClose={() => !hiring && setHireFor(null)}
                onSubmit={submitHire}
            />
            {testFor ? (
                <ExerciseModal
                    candidate={testFor}
                    onClose={() => setTestFor(null)}
                    onSuccess={() => {
                        patch(testFor.id, { status: "тест: отправлен" });
                        setTestFor(null);
                        onChange?.();
                    }}
                />
            ) : null}
        </>
    );

    return {
        busyId,
        withPatch,
        setStatus,
        reject,
        saveComment,
        openOffer: setOfferFor,
        openInterview: setInterviewFor,
        openTest: setTestFor,
        openHire: setHireFor,
        modals,
    };
}
